import { z } from 'zod'

export const TYPES_MODELES_COURRIEL = ['general', 'confirmation', 'rappel', 'paiement', 'contrat', 'approbation', 'annulation', 'connexion'] as const
export const TypeModeleCourriel = z.enum(TYPES_MODELES_COURRIEL)
export type TypeModeleCourriel = z.infer<typeof TypeModeleCourriel>

export const VARIABLES_COURRIEL = [
  { cle: 'organisation.nom', libelle: 'Nom de votre organisation', exemple: 'Ville de Granby' },
  { cle: 'organisation.courriel', libelle: 'Courriel de votre organisation', exemple: 'loisirs@example.test' },
  { cle: 'destinataire.nom', libelle: 'Nom du destinataire', exemple: 'Camille Roy' },
  { cle: 'destinataire.prenom', libelle: 'Prénom du destinataire', exemple: 'Camille' },
  { cle: 'destinataire.courriel', libelle: 'Courriel du destinataire', exemple: 'camille@example.test' },
  { cle: 'service.nom', libelle: 'Nom du service', exemple: 'Location de la salle communautaire' },
  { cle: 'courriel.sujet', libelle: 'Sujet du message automatique', exemple: 'Votre réservation est confirmée' },
] as const

const texte = (max: number) => z.string().max(max, `Ce texte dépasse ${max} caractères.`).refine(
  (valeur) => !/[\u0000-\u0008\u000b\u000c\u000e-\u001f\u007f]/.test(valeur),
  'Le texte contient un caractère de contrôle non pris en charge.',
)
const ligne = (max: number) => texte(max).refine((valeur) => !/[\r\n]/.test(valeur), 'Ce champ doit tenir sur une ligne.')
const modele = z.object({
  sujet: ligne(200),
  titre: ligne(200).trim().min(1, 'Ajoutez un titre au modèle.'),
  introduction: texte(4000),
  conclusion: texte(4000),
  bouton: ligne(80),
}).strict()

export const ConfigurationCourriels = z.object({
  identite: z.object({
    nom: ligne(120).trim().min(1, 'Indiquez le nom de votre organisation.'),
    logo_url: z.string().max(2000).refine((valeur) => {
      if (!valeur) return true
      try {
        const url = new URL(valeur)
        return url.protocol === 'https:' && !url.username && !url.password && !/[\s<>"']/.test(valeur)
      } catch { return false }
    }, 'Le logo doit être une adresse HTTPS publique, ou rester vide.'),
    couleur: z.string().regex(/^#[0-9a-fA-F]{6}$/, 'Choisissez une couleur au format #A32961.'),
    signature: texte(2000),
    pied_page: texte(1000),
  }).strict(),
  modeles: z.object({
    general: modele, confirmation: modele, rappel: modele, paiement: modele,
    contrat: modele, approbation: modele, annulation: modele, connexion: modele,
  }).strict(),
}).strict().superRefine((configuration, ctx) => {
  const champs = [configuration.identite.signature, configuration.identite.pied_page,
    ...Object.values(configuration.modeles).flatMap((m) => [m.sujet, m.titre, m.introduction, m.conclusion, m.bouton])]
  for (const champ of champs) {
    for (const match of champ.matchAll(/\{\{\s*([^{}]+?)\s*\}\}/g)) {
      if (!VARIABLES_COURRIEL.some((v) => v.cle === match[1])) ctx.addIssue({
        code: 'custom', message: `La variable « ${match[1]} » n’est pas disponible. Choisissez une variable dans la liste.`,
      })
    }
    if (/\{\{|\}\}/.test(champ.replace(/\{\{\s*[^{}]+?\s*\}\}/g, ''))) ctx.addIssue({
      code: 'custom', message: 'Une variable est incomplète. Utilisez deux accolades, par exemple {{destinataire.nom}}.',
    })
  }
})
export type ConfigurationCourriels = z.infer<typeof ConfigurationCourriels>
